import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AuthShell from "./AuthShell";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GoogleLogo, CircleNotch, Fingerprint } from "@phosphor-icons/react";
import { loginPasskey, reportPasskeyError } from "@/lib/passkey";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [stage, setStage] = useState("login"); // login | 2fa
  const [challenge, setChallenge] = useState(null);
  const [methods, setMethods] = useState([]);
  const [code, setCode] = useState("");
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.requires_2fa) {
        setChallenge(data.challenge_id);
        setMethods(data.methods || []);
        setStage("2fa");
        toast.info(data.methods?.includes("email") ? "We sent a 6-digit code to your email." : "Enter the code from your authenticator app.");
      } else {
        setUser(data);
        const next = new URLSearchParams(window.location.search).get("next");
        navigate(next && next.startsWith("/") ? next : "/dashboard");
      }
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const verify2fa = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/2fa/verify", { challenge_id: challenge, code });
      setUser(data);
      const next = new URLSearchParams(window.location.search).get("next");
      navigate(next && next.startsWith("/") ? next : "/dashboard");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const passkey = async () => {
    if (!email) { toast.error("Enter your email first, then use your passkey."); return; }
    try {
      const u = await loginPasskey(email);
      setUser(u);
      navigate("/dashboard");
    } catch (err) {
      const d = err.response?.data?.detail;
      if (err && err.name && !err.response) {
        // Browser WebAuthn error (distinguished by name), logged server-side.
        toast.error(await reportPasskeyError("login", err));
      } else {
        toast.error(d ? apiError(d) : "Passkey sign-in failed");
      }
    }
  };

  if (stage === "2fa") {
    const authMsg = methods.includes("totp") && methods.includes("email")
      ? "Enter the code from your authenticator app or the code we emailed you."
      : methods.includes("totp") ? "Enter the 6-digit code from your authenticator app."
      : "Enter the 6-digit code we emailed you.";
    return (
      <AuthShell title="Two-factor verification" subtitle={authMsg}>
        <form onSubmit={verify2fa} className="space-y-5">
          <div>
            <Label className="text-white/70">Verification code</Label>
            <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="000000"
              className="mt-2 bg-white/5 border-white/15 text-white text-center text-2xl tracking-[0.5em]" data-testid="twofa-code-input" />
            <p className="text-xs text-white/40 mt-2">Lost your device? Enter one of your backup codes.</p>
          </div>
          <Button type="submit" disabled={loading} className="w-full rounded-full h-11" data-testid="twofa-submit-btn">
            {loading ? <CircleNotch className="animate-spin" /> : "Verify & sign in"}
          </Button>
        </form>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Welcome back" subtitle="Sign in to your DuruVault.">
      <Seo title="Sign in" description="Sign in to your DuruVault to access your secure documents." path="/login" />
      <form onSubmit={submit} className="space-y-5">
        <div>
          <Label className="text-white/70">Email</Label>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@example.com"
            className="mt-2 bg-white/5 border-white/15 text-white" data-testid="login-email-input" />
        </div>
        <div>
          <div className="flex justify-between">
            <Label className="text-white/70">Password</Label>
            <Link to="/forgot-password" className="text-xs text-primary hover:underline" data-testid="login-forgot-link">Forgot?</Link>
          </div>
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••"
            className="mt-2 bg-white/5 border-white/15 text-white" data-testid="login-password-input" />
        </div>
        <Button type="submit" disabled={loading} className="w-full rounded-full h-11" data-testid="login-submit-btn">
          {loading ? <CircleNotch className="animate-spin" /> : "Sign in"}
        </Button>
      </form>
      <div className="flex items-center gap-3 my-6"><div className="h-px bg-white/10 flex-1" /><span className="text-white/40 text-xs">or</span><div className="h-px bg-white/10 flex-1" /></div>
      <Button onClick={google} variant="outline" className="w-full rounded-full h-11 bg-transparent border-white/15 text-white hover:bg-white/10" data-testid="login-google-btn">
        <GoogleLogo size={18} weight="bold" className="mr-2" /> Continue with Google
      </Button>
      <Button onClick={passkey} variant="outline" className="w-full rounded-full h-11 mt-3 bg-transparent border-white/15 text-white hover:bg-white/10" data-testid="login-passkey-btn">
        <Fingerprint size={18} weight="bold" className="mr-2" /> Sign in with a passkey
      </Button>
      <p className="mt-8 text-center text-sm text-white/50">
        New to DuruVault? <Link to="/register" className="text-primary hover:underline" data-testid="login-register-link">Create an account</Link>
      </p>
    </AuthShell>
  );
}
